'use client'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import {
  getListPromotion,
  getListPromotionV2,
  getListPromotionWithoutCart,
} from '../services'

export const usePromotion = ({
  cartId,
  sku,
  qty = 1,
  voucher,
  enabled = true,
}: {
  cartId: string
  sku: string
  qty?: number
  voucher: string
  enabled?: boolean
}) => {
  const previousCartId = useRef<string>(cartId)

  const query = useQuery({
    queryKey: ['list-promotion', cartId, sku, qty, voucher],
    queryFn: () => {
      return getListPromotion({ cartId: cartId, sku, qty, voucher })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
    retry: 2, // Retry failed requests 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    enabled: enabled && !!cartId && cartId.length > 0 && !!sku,
    refetchOnMount: true, // Refetch khi component mount
    refetchOnWindowFocus: false, // Không refetch khi focus window
    placeholderData: (previousData) => previousData,
  })

  const { refetch } = query

  // Auto refetch khi cartId thay đổi từ empty/undefined sang có giá trị
  useEffect(() => {
    const isNowValid = !!cartId && cartId.length > 0
    const hasChanged = previousCartId.current !== cartId

    if (hasChanged && isNowValid) {
      refetch()
    }

    previousCartId.current = cartId
  }, [cartId, refetch])

  return query
}

export const useMultiPromotion = ({
  cartId,
  sku,
  listQuantity,
  voucher,
  enabled = true,
}: {
  cartId: string
  sku: string
  listQuantity: number[]
  voucher: string
  enabled?: boolean
}) => {
  const results = useQueries({
    queries: [
      ...listQuantity.map((qty) => ({
        queryKey: ['list-promotion', cartId, sku, qty, voucher],
        queryFn: () => {
          return getListPromotion({ cartId: cartId, sku, qty, voucher })
        },
        enabled: enabled && !!cartId && !!sku,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      })),
    ],
    combine: (results) => {
      return {
        data: results?.map((result) => result?.data),
        isLoading: results?.some((result) => result?.isLoading),
      }
    },
  })
  return results
}

export const usePromotionV2 = ({ cartId, enabled = true }: { cartId: string; enabled?: boolean }) => {
  const query = useQuery({
    queryKey: ['list-promotion-v2', cartId],
    queryFn: () => {
      return getListPromotionV2({ cartId: cartId })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
    retry: 2, // Retry failed requests 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    enabled: enabled && !!cartId && cartId.length > 0,
    refetchOnMount: true, // Refetch khi component mount
    refetchOnWindowFocus: false, // Không refetch khi focus window
    placeholderData: (previousData) => previousData,
  })

  return query
}

export const usePromotionWithoutCart = ({
  sku,
  qty = 1,
  voucher,
  enabled = true,
}: {
  sku: string
  qty?: number
  voucher: string
  enabled?: boolean
}) => {
  return useQuery({
    queryKey: ['list-promotion', 'list-promotion-without-cart', sku, qty, voucher],
    queryFn: () => getListPromotionWithoutCart({ sku, qty, voucher }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
    retry: 2, // Retry failed requests 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    enabled: enabled && !!sku,
    refetchOnMount: true, // Refetch khi component mount
    refetchOnWindowFocus: false, // Không refetch khi focus window
    placeholderData: (previousData) => previousData,
  })
}

export const useMultiPromotionWithoutCart = ({
  sku,
  listQuantity,
  voucher,
  enabled = true,
}: {
  sku: string
  listQuantity: number[]
  voucher: string
  enabled?: boolean
}) => {
  const results = useQueries({
    queries: [
      ...listQuantity.map((qty) => ({
        queryKey: ['list-promotion', 'list-promotion-without-cart', sku, qty, voucher],
        queryFn: () => {
          return getListPromotionWithoutCart({ sku, qty, voucher })
        },
        enabled: enabled && !!sku,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      })),
    ],
    combine: (results) => {
      return {
        data: results?.map((result) => result?.data),
        isLoading: results?.some((result) => result?.isLoading),
      }
    },
  })
  return results
}

export const useClearCachingPromotion = () => {
  const queryClient = useQueryClient()
  const clear = () => {
    queryClient.removeQueries({
      queryKey: ['list-promotion'],
    })
    queryClient.removeQueries({
      queryKey: ['list-promotion-v2'],
    })
  }
  return { clear }
}
